import { useState, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import { DEFAULT_LANGUAGE } from 'config/languages';
import Accordion from 'lib/components/accordion/Accordion';
import { formatQuestionOrder } from 'lib/components/question-detail/dataTransform';
import QuestionDetail from 'lib/components/question-detail/QuestionDetail';
import StatusPill, {
  STATUS_PILL_VARIANT,
} from 'lib/components/status-pill/StatusPill';
import Tooltip from 'lib/components/tooltip/Tooltip';

import { ReactComponent as PencilOutline } from 'assets/images/pencil-outline.svg';

import './SettingsQuestionGroup.scss';
import {
  QUESTION_GROUP_STATUS,
  QUESTION_GROUP_STATUS_LABEL,
} from 'config/questionOptions';
import settingsService from 'modules/settings/settingsService';
import { questionGroupToDetailState } from 'modules/settings/dataTransform';

const QUESTION_COLUMNS = [
  {
    title: '',
    className: 'edit-cell',
    textAlign: 'center',
  },
  {
    title: '#',
    className: 'id-cell',
    textAlign: 'left',
  },
  {
    title: 'Question',
    className: 'question-cell',
    textAlign: 'left',
  },
  {
    title: 'Question Type',
    className: 'question-type-cell',
    textAlign: 'left',
  },
  {
    title: 'Sample Size',
    className: 'sample-size-cell',
    textAlign: 'right',
  },
  {
    title: '',
    className: 'toggle-cell',
    textAlign: 'right',
  },
];

const QuestionGroupTitle = ({ data, editable, onEditClick }) => (
  <>
    <div className='question-group-name'>
      {data.name.value}
      {editable && (
        <button
          type='button'
          onClick={onEditClick}
          className='edit-question-group-button'
        >
          <Tooltip overlay='Edit question group' placement='top'>
            <PencilOutline />
          </Tooltip>
        </button>
      )}
    </div>
    <div className='question-group-details'>
      {!!data.order.value && (
        <div className='question-group-order'>
          <span>Order:</span>
          <StatusPill variant={STATUS_PILL_VARIANT.NOTIFY}>
            {data.order.value}
          </StatusPill>
        </div>
      )}
      {!!data.tags?.value?.length && (
        <div className='question-group-tags'>
          <span>Tags:</span>
          {(data.tags.value || []).map((tag, idx) => (
            <StatusPill key={idx} variant={STATUS_PILL_VARIANT.NOTIFY}>
              {tag.label}
            </StatusPill>
          ))}
        </div>
      )}
      {!!data.assetTypes?.value?.length && (
        <div className='question-group-asset-types'>
          <span>Asset types:</span>
          {(data.assetTypes.value || []).map((type, idx) => (
            <StatusPill key={idx} variant={STATUS_PILL_VARIANT.NOTIFY}>
              {type.label}
            </StatusPill>
          ))}
        </div>
      )}
      {!!data.sourceTypes?.value?.length && (
        <div className='question-group-source-types'>
          <span>Source types:</span>
          {(data.sourceTypes.value || []).map((type, idx) => (
            <StatusPill key={idx} variant={STATUS_PILL_VARIANT.NOTIFY}>
              {type.label}
            </StatusPill>
          ))}
        </div>
      )}
      {!!data.inspectionTypes?.value?.length && (
        <div className='question-group-inspection-types'>
          <span>Inspection types:</span>
          {(data.inspectionTypes.value || []).map((type, idx) => (
            <StatusPill key={idx} variant={STATUS_PILL_VARIANT.NOTIFY}>
              {type.label}
            </StatusPill>
          ))}
        </div>
      )}
      {!!data.customAttributes?.value?.length && (
        <div className='question-group-custom-attributes'>
          <span>Custom attributes:</span>
          {(data.customAttributes.value || []).map((attr, idx) => (
            <StatusPill key={idx} variant={STATUS_PILL_VARIANT.NOTIFY}>
              {attr.groupLabel}:{' '}
              <strong>{attr?.label || `Any (${attr.groupLabel})`}</strong>
            </StatusPill>
          ))}
        </div>
      )}
      {!!data.companyAttributes?.value?.length && (
        <div className='question-group-company-attributes'>
          <span>Company attributes:</span>
          {(data.companyAttributes.value || []).map((attr, idx) => (
            <StatusPill key={idx} variant={STATUS_PILL_VARIANT.NOTIFY}>
              {attr.groupLabel}:{' '}
              <strong>{attr?.label || `Any (${attr.groupLabel})`}</strong>
            </StatusPill>
          ))}
        </div>
      )}
    </div>
  </>
);

const SettingsQuestionGroup = ({
  vm,
  data,
  editable,
  initialOpen,
  className,
  onEditClick,
  hasErrors = false,
  ...props
}) => {
  const [isOpen, setOpen] = useState(initialOpen ?? true);
  const [qgtData, setQgtData] = useState(null);
  const [qgtDataLoading, setQgtDataLoading] = useState(false);

  const loadQgt = useCallback(() => {
    setQgtDataLoading(true);
    settingsService.getQuestionGroupTemplateById(data.id).then((res) => {
      if (res?.id && vm?.state?.questionOptions) {
        const qgtTransformed = questionGroupToDetailState(res, vm.state.questionOptions);
        setQgtData(qgtTransformed);
      } else {
        setQgtData(null);
      }
    }).catch((e) => {
      console.error('Error fetching Question Group Data', e);
      setQgtData(null);
    }).finally(() => {
      setQgtDataLoading(false)
    });
  }, [JSON.stringify([data?.id, vm?.questionOptions])])

  useEffect(() => {
    if (data?.id && (isOpen || hasErrors)) {
      loadQgt();
    } else {
      setQgtData(null);
    }
  }, [isOpen, hasErrors, data?.id, loadQgt]);

  const actions =
    data.status?.value === QUESTION_GROUP_STATUS.DRAFT ? (
      <StatusPill variant={STATUS_PILL_VARIANT.WARNING}>
        {QUESTION_GROUP_STATUS_LABEL[QUESTION_GROUP_STATUS.DRAFT]}
      </StatusPill>
    ) : null;

  const renderEmptyPlaceholder = () => {
    let emptyMsg = null;

    if (!qgtDataLoading && !qgtData?.questions?.length) {
      emptyMsg = 'Question group is empty';
    }

    return emptyMsg?.length ? (
      <div className='empty'>{emptyMsg}</div>
    ) : null;
  }

  return (
    <Accordion
      title={
        <QuestionGroupTitle
          onEditClick={onEditClick}
          editable={editable}
          data={data}
        />
      }
      actions={actions}
      isOpen={isOpen || hasErrors}
      onToggle={() => setOpen(!isOpen)}
      className={classNames('qm-settings-question-group', className)}
      {...props}
    >
      <div className='questions-table'>
        {qgtData?.questions?.length && (
          <div className={classNames('table-row table-header')}>
            {QUESTION_COLUMNS.map((col, idx) => (
              <div
                key={idx}
                className={classNames('table-cell', col.className, {
                  [`align-${col.textAlign}`]: col.textAlign,
                })}
              >
                {col.title}
              </div>
            ))}
          </div>
        )}
        {qgtData?.questions?.map((groupedQuestion, idx) => (
          <QuestionDetail
            language={DEFAULT_LANGUAGE}
            questionOptions={vm.state.questionOptions}
            key={idx}
            data={{
              ...groupedQuestion,
              dependencyQuestion:
                qgtData.questions.find(
                  (q) =>
                    q.order ===
                    groupedQuestion.dependencyQuestionTemplateOrder,
                ) ?? null,
            }}
            id={formatQuestionOrder(groupedQuestion)}
          />
        ))}
        {renderEmptyPlaceholder()}
      </div>
    </Accordion>
  );
};

export default SettingsQuestionGroup;
